// screenTime.js
// Bridge to the in-app ScreenTime Capacitor plugin (ios/App/App/
// ScreenTimePlugin.swift) and the sync path from App Group day records into
// the `screen_time_days` table. Only runs on iOS native; no-ops elsewhere.
//
// Day records are written by the DeviceActivityMonitor extension as
// DELIVERED-callback facts (see the migration header for the gating design:
// cap crossed = over; closed at natural end + no cap = under; neither =
// unknown, never success). This file moves facts; it derives nothing — the
// derivation lives in the goal-progress matched pair.

import { registerPlugin, Capacitor } from '@capacitor/core';
import supabase from '../supabase';
import { reportError } from '../sentry';

const ScreenTime = registerPlugin('ScreenTime');

const isNative = () => Capacitor.isNativePlatform();

export async function screenTimeStatus() {
  if (!isNative()) return { available: false };
  try {
    return await ScreenTime.getStatus();
  } catch (e) {
    // Plugin missing (old binary) reads as unavailable, never as an error.
    return { available: false };
  }
}

/// Authorization + picker, in order. Returns { ok, reason } — reason is one
/// of 'unavailable' | 'denied' | 'no_selection' when ok is false.
/// reuseSelection (default true): if the device already has a saved cap
/// selection, skip the picker and reuse it. Creating a second Screen Time
/// goal, or repairing one, should not force the user to re-pick the apps they
/// already chose. Pass { reuseSelection: false } to always show the picker
/// (e.g. an explicit "change apps" action).
export async function setupScreenTime({ reuseSelection = true } = {}) {
  if (!isNative()) return { ok: false, reason: 'unavailable' };
  try {
    const auth = await ScreenTime.requestAuthorization();
    if (auth.authorization !== 'approved') return { ok: false, reason: auth.authorization === 'unavailable' ? 'unavailable' : 'denied' };
    if (reuseSelection) {
      const status = await ScreenTime.getStatus();
      if (status?.hasSelection) {
        // Reuse the saved selection; report its counts from status so the
        // goal row's source_config stays honest without re-prompting.
        return { ok: true, reused: true, apps: status.selApps || 0, categories: status.selCategories || 0, webDomains: status.selWebDomains || 0 };
      }
    }
    const picked = await ScreenTime.presentPicker();
    if (!picked.hasSelection) return { ok: false, reason: 'no_selection' };
    // Counts only — the FamilyActivitySelection tokens themselves are opaque
    // device-scoped blobs that never leave the App Group. The counts are the
    // representable part, and the goal row records them in source_config.
    return { ok: true, apps: picked.apps || 0, categories: picked.categories || 0, webDomains: picked.webDomains || 0 };
  } catch (e) {
    console.error('[screenTime] setup error:', e);
    reportError(e, { op: 'screenTime.setup' });
    return { ok: false, reason: 'unavailable' };
  }
}

// ─── ACCOUNT OWNERSHIP ──────────────────────────────────────────────────────
// The App Group is device-wide (see ScreenTimeShared.swift). These keep one
// account's Screen Time state from showing — or syncing — under another.

/// Stamp the signed-in account as the owner of this device's Screen Time
/// state. Called after any successful setup/arm.
export async function claimScreenTime(userId) {
  if (!isNative() || !userId) return;
  try { await ScreenTime.claim({ userId }); }
  catch (e) { console.error('[screenTime] claim error:', e); }
}

/// Stop monitoring and wipe all App Group Screen Time keys.
export async function resetScreenTime() {
  if (!isNative()) return;
  try { await ScreenTime.reset(); }
  catch (e) { console.error('[screenTime] reset error:', e); }
}

/// Reconcile device Screen Time ownership against the signed-in account BEFORE
/// its state is trusted (shown or synced). Mirrors appLock's
/// resolveLockStateForUser:
///   - owner matches this user            → keep it (return { owned: true }).
///   - owner set but a DIFFERENT account  → foreign state: reset, and this
///                                           user starts clean ({ reset: true }).
///   - owner empty, but state exists      → legacy/pre-owner single-account
///                                           device: ADOPT (claim), never wipe.
///   - nothing configured                 → claim, so future writes attribute.
/// Returns quickly and never throws.
export async function reconcileScreenTimeOwner(userId) {
  if (!isNative() || !userId) return { skipped: true };
  try {
    const status = await ScreenTime.getStatus();
    if (!status?.available) return { skipped: true };
    const owner = status.owner || '';
    if (owner === userId) return { owned: true };
    if (owner && owner !== userId) {
      await ScreenTime.reset();
      // Own the clean slate immediately so this account's first arm is
      // attributed without waiting for the next reconcile.
      await ScreenTime.claim({ userId });
      return { reset: true };
    }
    // Empty owner: adopt for this account (whether or not state exists — an
    // empty device just gets its owner stamped for next time).
    const hadState = !!(status.enabled || status.usageEnabled || status.hasSelection || status.hasUsageSelection || status.armedToday);
    await ScreenTime.claim({ userId });
    return { adopted: hadState };
  } catch (e) {
    console.error('[screenTime] reconcile error:', e);
    return { error: true };
  }
}

/// Full-day usage display setup: authorization, then the select-all usage
/// picker, then enable (which re-arms today with the 32-step ladder).
/// Same contract as setupScreenTime: { ok, reason } with reason
/// 'unavailable' | 'denied' | 'no_selection', never a throw.
export async function setupUsageDisplay() {
  if (!isNative()) return { ok: false, reason: 'unavailable' };
  try {
    const auth = await ScreenTime.requestAuthorization();
    if (auth.authorization !== 'approved') return { ok: false, reason: auth.authorization === 'unavailable' ? 'unavailable' : 'denied' };
    const picked = await ScreenTime.presentUsagePicker();
    if (!picked.hasSelection) return { ok: false, reason: 'no_selection' };
    await ScreenTime.setUsageEnabled({ enabled: true });
    return { ok: true };
  } catch (e) {
    console.error('[screenTime] usage setup error:', e);
    reportError(e, { op: 'screenTime.usageSetup' });
    return { ok: false, reason: 'unavailable' };
  }
}

/// Broken-chain self-heal for USAGE-ONLY users (no cap goal): re-arms
/// today's window with the ladder. Caller gates on status (enabled +
/// selection + approved + not armed today) so this never churns windows.
export async function rearmUsage() {
  if (!isNative()) return;
  try {
    await ScreenTime.setUsageEnabled({ enabled: true });
  } catch (e) {
    console.error('[screenTime] rearmUsage error:', e);
    reportError(e, { op: 'screenTime.rearmUsage' });
  }
}

export async function armScreenTime(capMinutes) {
  if (!isNative()) return { armed: false, reason: 'unavailable' };
  try {
    // Missing authorization or selection are EXPECTED states, not
    // exceptions: a fresh install, a Settings revoke, or an OS update
    // resetting Family Controls (SCORIFY-APP-6 was the arm-on-open effect
    // hitting exactly this 4.6s after launch on iOS 26.6.2 — the plugin's
    // reject became a reported error with no UI). Pre-check and return a
    // reason the caller can surface; only real failures reach Sentry.
    const status = await ScreenTime.getStatus();
    if (!status?.available) return { armed: false, reason: 'unavailable' };
    if (status.authorization !== 'approved') return { armed: false, reason: 'not_authorized' };
    if (!status.hasSelection) return { armed: false, reason: 'no_selection' };
    return await ScreenTime.arm({ capMinutes: Number(capMinutes) });
  } catch (e) {
    console.error('[screenTime] arm error:', e);
    reportError(e, { op: 'screenTime.arm' });
    return { armed: false, error: String(e?.message || e) };
  }
}

export async function disarmScreenTime() {
  if (!isNative()) return;
  try {
    await ScreenTime.disarm();
  } catch (e) {
    console.error('[screenTime] disarm error:', e);
    reportError(e, { op: 'screenTime.disarm' });
  }
}

// ─── SYNC: App Group day records → screen_time_days ─────────────────────────
let _syncInflight = null; // { userId, promise }

export function syncScreenTime(userId) {
  if (_syncInflight && _syncInflight.userId === userId) return _syncInflight.promise;
  const promise = _doSync(userId).finally(() => { _syncInflight = null; });
  _syncInflight = { userId, promise };
  return promise;
}

async function _doSync(userId) {
  try {
    if (!userId || !isNative()) return;

    let days = [];
    try {
      const result = await ScreenTime.readDays();
      days = Array.isArray(result?.days) ? result.days : [];
    } catch {
      return; // plugin missing — old binary, nothing to sync
    }
    if (days.length === 0) return;

    // armed_at (openedAt) and cap_minutes are NOT NULL in the table: a
    // record without them never had a window open under a known cap, and a
    // row we cannot attribute honestly does not get written.
    // cap_minutes is NOT NULL but usage-only days have no cap: 0 is the
    // recorded "no cap armed" value (the resolver keys caps on the goal's
    // period_target fallback, and cap_crossed_at is what means anything).
    const rows = days
      .filter(d => d.date && d.openedAt && (d.capMinutes || d.usageStepMinutes))
      .map(d => ({
        user_id: userId,
        date: d.date,
        armed_at: d.openedAt,
        // Per-window starts (usage/cap split): each drives its own resolver so
        // a cap re-arm never moves the usage clamp base. Null for pre-split
        // records — both resolvers fall back to armed_at.
        usage_armed_at: d.usageOpenedAt || null,
        cap_armed_at: d.capOpenedAt || null,
        closed_at: d.closedAt || null,
        heartbeat_at: d.heartbeatAt || null,
        midpoint_at: d.midpointAt || null,
        cap_warning_at: d.capWarningAt || null,
        cap_crossed_at: d.capCrossedAt || null,
        cap_minutes: d.capMinutes || 0,
        heartbeat_minutes: d.heartbeatMinutes || 5,
        usage_step_minutes: d.usageStepMinutes || null,
        usage_steps_fired: d.usageStepMinutes != null ? (d.usageStepsFired || 0) : null,
        last_step_at: d.lastStepAt || null,
        source: 'screen_time',
        // The table has no auto-update trigger, so without this the column
        // freezes at insert time and reads as "never updated since arm".
        updated_at: new Date().toISOString(),
      }));
    if (rows.length === 0) return;

    const { error } = await supabase
      .from('screen_time_days')
      .upsert(rows, { onConflict: 'user_id,date', ignoreDuplicates: false });
    if (error) {
      console.error('[screenTime] upsert error:', error);
      // Row count only — never the day records themselves.
      reportError(new Error(`screen_time_days upsert: ${error.message}`), {
        op: 'screenTime.upsert', code: error.code, rows: rows.length,
      });
      return; // keep App Group records; retry next open
    }

    // Post-sync hygiene: drop App Group records older than 30 days. Synced
    // rows in Supabase are the durable copy.
    const cutoff = new Date(Date.now() - 30 * 86400000);
    const before = `${cutoff.getFullYear()}-${String(cutoff.getMonth() + 1).padStart(2, '0')}-${String(cutoff.getDate()).padStart(2, '0')}`;
    try { await ScreenTime.pruneDays({ before }); } catch {}

    console.log('[screenTime] synced', rows.length, 'day(s)');
    // Built-in proof markers: the extension's diagnostic lines (step
    // deliveries, clamps, closes, chains) dumped to the JS console so a
    // device pass with Xcode attached validates the real feature — no
    // throwaway harness. Read-only; the extension caps the list.
    try {
      const diag = await ScreenTime.readDiagnostics();
      const lines = Array.isArray(diag?.lines) ? diag.lines.slice(-40) : [];
      if (lines.length) console.log('[screenTime] diagnostics:\n' + lines.join('\n'));
    } catch {}
    // Same pattern as scorify-health-synced: the dashboard refetches the
    // table on this signal so cards can never show a stale vintage.
    try { window.dispatchEvent(new CustomEvent('scorify-screentime-synced')); } catch {}
  } catch (err) {
    console.error('[screenTime] unhandled sync error:', err);
    reportError(err, { op: 'screenTime.sync.unhandled' });
  }
}
